import React from 'react';
import { Box, Typography, Link as MuiLink, Chip, Stack, Card, CardActionArea, CardContent, Divider } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getGuideBySlug, relatedGuides } from '../content/guides';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { AUTHOR, SITE_URL, useJsonLd } from '../hooks/useJsonLd';
import MarkdownContent from '../components/MarkdownContent/MarkdownContent';
import AffiliateRecommendations from '../components/Affiliate/AffiliateRecommendations';

const GuideArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = getGuideBySlug(slug ?? '');

  useDocumentMeta({
    title: guide ? `${guide.title} | PDF-To-Text.com` : 'Guide Not Found',
    description: guide?.description,
    canonicalPath: guide ? `/guides/${guide.slug}` : undefined,
    ogType: guide ? 'article' : undefined,
  });
  const url = guide ? `${SITE_URL}/guides/${guide.slug}` : '';
  useJsonLd(
    'guide',
    guide
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'TechArticle',
            headline: guide.title,
            description: guide.description,
            datePublished: guide.publishedDate,
            dateModified: guide.publishedDate,
            author: AUTHOR,
            publisher: { '@type': 'Organization', name: 'PDF-To-Text.com', url: SITE_URL, logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo512.png` } },
            mainEntityOfPage: url,
            url,
            articleSection: guide.category,
            image: `${SITE_URL}/og-image.png`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'Guides', item: `${SITE_URL}/guides` },
              { '@type': 'ListItem', position: 3, name: guide.title, item: url },
            ],
          },
        ]
      : null
  );

  if (!guide) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Guide not found</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          That guide doesn't exist, or may have moved.
        </Typography>
        <MuiLink component={RouterLink} to="/guides">Back to all guides</MuiLink>
      </Box>
    );
  }

  const related = relatedGuides(guide);

  return (
    <Box component="article">
      <MuiLink component={RouterLink} to="/guides" variant="body2" sx={{ display: 'inline-block', mb: 1 }}>
        ← All guides
      </MuiLink>
      <Typography variant="h4" component="h1" gutterBottom>{guide.title}</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip size="small" label={guide.category} variant="outlined" />
        <Typography variant="caption" color="text.secondary">{guide.publishedDate}</Typography>
      </Stack>
      <MarkdownContent content={guide.body} />

      {related.length > 0 && (
        <Box component="aside" sx={{ mt: 5 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" component="h2" gutterBottom>Related guides</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {related.map((g) => (
              <Card key={g.slug} variant="outlined">
                <CardActionArea component={RouterLink} to={`/guides/${g.slug}`} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{g.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{g.category}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      <AffiliateRecommendations category={guide.affiliateCategory} />
    </Box>
  );
};

export default GuideArticlePage;
